import React, {useState, useEffect, useCallback } from 'react';
import {
  BuyStallPageContainer,
  MessageContainer,
  Spinner,
} from './styles/BuyStall.styles';
import StallRegistrationForm from './StallRegistrationForm';
import StallPaymentReview from './StallPaymentReview';
import TermsAndConditionsModal from './TermsAndConditionsModal';
import {db, serverTimestamp, storage } from '../../firebase';
import {collection, addDoc, doc, setDoc, getDocs, updateDoc, getDoc } from 'firebase/firestore';
import {ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {useNavigate, useSearchParams } from 'react-router-dom';

// Type definitions and constants are declared here to keep the component body clean.
export type SubscriptionDuration = 'Weekly' | 'Monthly' | '6-Monthly' | '12-Monthly' | '';
export interface StallFormData {
  gremastaff: string;
  stallNumber: string;
  isActive: boolean;
  businessName: string;
  contactPerson: string;
  email: string;
  businessPhone: string;
  whatsappPhone: string;
  userlisting: string;
  website: string;
  selectedCountry: string;
  selectedRegion: string;
  selectedDistrict: string;
  selectedSubcounty: string;
  selectedParish: string;
  selectedVillage: string;
  actualLocation: string;
  category: string;
  businessPhoto: File | null;
  businessPhotoPreview: string | null;
  googleMapLocation: string;
  additionalDetails: string;
  stallType: 'Premium' | 'Standard' | '';
  subscriptionDuration: SubscriptionDuration;
  subscriptionAmount: number;
  paymentStatus: 'pending' | 'SUCCESS' | 'FAILED' | 'initiated';
  transactionId: string | null;
  createdAt: any;
  tiktokLink: string;
  facebookLink: string;
  xTwitterLink: string;
  linkedinLink: string;
  youtubeLink: string;
  instagramLink: string;
  subscriptionExpiryDate?: Date;
    latitude: number | null;
  longitude: number | null;
}

interface VillageList {
  [parish: string]: string[];
}
interface SubcountyData {
  [subcounty: string]: VillageList;
}
interface DistrictData {
  [district: string]: SubcountyData;
}
interface LocationData {
  [region: string]: DistrictData;
}


const getDurationInDays = (duration: SubscriptionDuration) => {
  switch (duration) {
    case 'Weekly':
      return 7;
    case 'Monthly':
      return 30;
    case '6-Monthly':
      return 180;
    case '12-Monthly':
      return 365;
    default:
      return 0;
  }
};

const generateStallNumber = async (): Promise<string> => {
  const counterRef = doc(db, "counters", "stallNumber");

  // Get or initialize
  const snap = await getDoc(counterRef);
  let nextNumber = 1;

  if (snap.exists()) {
    const data = snap.data();
    nextNumber = (data.last || 0) + 1;
    await setDoc(counterRef, { last: nextNumber }, { merge: true });
  } else {
    await setDoc(counterRef, { last: 1 });
  }

  return `GREMA-${nextNumber.toString().padStart(2, "0")}`;
};

const BuyStall: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<StallFormData>({
    stallNumber: '',
    isActive: false,
    businessName: '',
    contactPerson: '',
    email: '',
    businessPhone: '',
    whatsappPhone: '',
    website: '',
    userlisting: '',
    selectedRegion: '',
    selectedDistrict: '',
    selectedSubcounty: '',
    selectedParish: '',
    selectedVillage: '',
    actualLocation: '',
    category: '',
    businessPhoto: null,
    businessPhotoPreview: null,
    googleMapLocation: '',
    additionalDetails: '',
    stallType: '',
    subscriptionDuration: '',
    subscriptionAmount: 0,
    paymentStatus: 'initiated',
    transactionId: null,
    createdAt: null,
    tiktokLink: '',
    facebookLink: '',
    xTwitterLink: '',
    selectedCountry:'Uganda',
    linkedinLink: '',
    youtubeLink: '',
    instagramLink: '',
      latitude:0,
  longitude:0,
  gremastaff:'',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showTermsAndConditions, setShowTermsAndConditions] = useState(true);

  const [locationData, setLocationData] = useState<LocationData | null>(null);
  const [locationLoading, setLocationLoading] = useState(true);
  const [locationError, setLocationError] = useState<string | null>(null);

  const [basePrices, setBasePrices] = useState<{ Premium: number; Standard: number }>({
    Premium: 200000, // Default fallback
    Standard: 50000,
  });
  const [pricesLoading, setPricesLoading] = useState(true);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Compute pricing model based on base prices
  const pricingModel = {
    Premium: {
      Weekly: basePrices.Premium / 4,
      Monthly: basePrices.Premium,
      '6-Monthly': Math.round(basePrices.Premium * 6 * 0.8),
      '12-Monthly': Math.round(basePrices.Premium * 12 * 0.7),
    },
    Standard: {
      Weekly: basePrices.Standard / 4,
      Monthly: basePrices.Standard,
      '6-Monthly': Math.round(basePrices.Standard * 6 * 0.8),
      '12-Monthly': Math.round(basePrices.Standard * 12 * 0.7),
    },
  };

  const subscriptionAmount = formData.stallType && formData.subscriptionDuration
    ? pricingModel[formData.stallType][formData.subscriptionDuration]
    : 0;

  const updateFormData = useCallback((newData: Partial<StallFormData> | ((prev: StallFormData) => Partial<StallFormData>)) => {
    setFormData(prev => {
      if (typeof newData === 'function') {
        return {...prev, ...newData(prev) };
      }
      return {...prev, ...newData };
    });
  }, []);

  const handleAgreeToTerms = () => {
    setShowTermsAndConditions(false);
  };
  
  // These functions must be defined BEFORE the useEffect that uses them.
  const handlePaymentSuccess = useCallback(async (transactionId: string, stallNumber: string) => {
    setLoading(true);
    setSuccessMessage('Payment successful! Your stall is now active.');
    setError(null);
    try {
      const stallDocRef = doc(db, 'stalls', stallNumber);
      const stallDoc = await getDoc(stallDocRef);
      if (!stallDoc.exists()) {
        console.error("Stall document not found for successful payment:", stallNumber);
        setError('Payment was successful, but we could not find your stall to update. Please contact support.');
        setLoading(false);
        return;
      }
      const stallData = stallDoc.data() as StallFormData;
      const expiryDate = new Date();
      if (stallData.subscriptionDuration === 'Weekly') {
        expiryDate.setDate(expiryDate.getDate() + 7);
      } else if (stallData.subscriptionDuration === 'Monthly') {
        expiryDate.setMonth(expiryDate.getMonth() + 1);
      } else if (stallData.subscriptionDuration === '6-Monthly') {
        expiryDate.setMonth(expiryDate.getMonth() + 6);
      } else if (stallData.subscriptionDuration === '12-Monthly') {
        expiryDate.setFullYear(expiryDate.getFullYear() + 1);
      }
      await updateDoc(stallDocRef, {
        paymentStatus: 'SUCCESS',
        transactionId: transactionId,
        isActive: true,
        subscriptionExpiryDate: expiryDate,
      });
      console.log('Stall successfully activated and updated in Firestore.');
      navigate('/user-listings');
    } catch (err) {
      console.error('Error updating stall with payment success:', err);
      setError('Payment was successful, but we failed to update your stall details. Please contact support.');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const handlePaymentFailure = useCallback(async (transactionId: string, stallNumber: string) => {
    setLoading(true);
    setError('Payment failed or was cancelled.');
    try {
      const stallDocRef = doc(db, 'stalls', stallNumber);
      await updateDoc(stallDocRef, {
        paymentStatus: 'FAILED',
        transactionId: transactionId,
      });
      console.log('Stall payment status updated to FAILED in Firestore.');
    } catch (err) {
      console.error('Error updating failed payment status:', err);
    } finally {
      setLoading(false);
    }
  }, []);
  
  // This useEffect must be placed after the useCallback functions it depends on.
  useEffect(() => {
    const orderTrackingId = searchParams.get('OrderTrackingId');
    const paymentStatus = searchParams.get('status');

    if (orderTrackingId && paymentStatus) {
      console.log(`Received payment callback. OrderTrackingId: ${orderTrackingId}, Status: ${paymentStatus}`);
      const stallNumber = orderTrackingId;
      
      if (paymentStatus === 'COMPLETED') {
        handlePaymentSuccess(orderTrackingId, stallNumber);
      } else {
        handlePaymentFailure(orderTrackingId, stallNumber);
      }
    }
  }, [searchParams, handlePaymentSuccess, handlePaymentFailure]);

  const saveStallDetailsAndImageToFirestore = useCallback(async (
    currentFormData: StallFormData,
    paymentTxId: string | null,
    currentPaymentStatus: StallFormData['paymentStatus'],
    imageUrl: string | null = null,
    filename: string | null = null,
    collectionName: string = 'stalls'
  ) => {
    setLoading(true);
    setError(null);
    try {
      const calculatedSubscriptionAmount = currentFormData.stallType && currentFormData.subscriptionDuration
        ? pricingModel[currentFormData.stallType][currentFormData.subscriptionDuration]
        : 0;
      const stallDocRef = doc(db, collectionName, currentFormData.stallNumber);
      const dataToSave: Partial<StallFormData> = {
        ...currentFormData,
        subscriptionAmount: calculatedSubscriptionAmount,
        paymentStatus: currentPaymentStatus,
        transactionId: paymentTxId,
        createdAt: serverTimestamp(),
      };
      delete dataToSave.businessPhoto;
      delete dataToSave.businessPhotoPreview;
      dataToSave.businessPhotoPreview = imageUrl;
      await setDoc(stallDocRef, dataToSave, {merge: true });
      console.log(`${collectionName} document written with ID: `, currentFormData.stallNumber);
      if (imageUrl && filename && collectionName === 'stalls') {
        const imagesCollectionRef = collection(stallDocRef, 'images');
        await addDoc(imagesCollectionRef, {
          url: imageUrl,
          filename: filename,
          uploadedAt: serverTimestamp(),
        });
        console.log("Image added to subcollection for stall: ", currentFormData.stallNumber);
      }
      return stallDocRef;
    } catch (e: any) {
      console.error("Error adding/updating document: ", e);
      setError('Failed to save stall details. ' + e.message);
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  const handleFormSubmit = async () => {
    setError(null);
    setLoading(true);
    if (!formData.businessPhoto) {
      setError('A business photo is required.');
      setLoading(false);
      return;
    }
    if (!formData.subscriptionDuration) {
      setError('Please select a subscription period.');
      setLoading(false);
      return;
    }
    if (!formData.stallType) {
      setError('Please select a stall type (Premium or Standard).');
      setLoading(false);
      return;
    }
    try {
      const filename = formData.businessPhoto.name;
      const imageRef = ref(storage, `stall-images/${formData.stallNumber}/${filename}`);
      await uploadBytes(imageRef, formData.businessPhoto);
      const imageUrl = await getDownloadURL(imageRef);
      
      // Save to pendingStalls with all necessary information
      const stallDocRef = await saveStallDetailsAndImageToFirestore(
        formData,
        null,
        'initiated', // Payment status is 'initiated' before payment
        imageUrl,
        filename,
        'pendingStalls'
      );
      
      console.log('Stall data saved to pendingStalls with ID:', formData.stallNumber);
      console.log('Stall document reference:', stallDocRef.path);
      
      // Verify the stall was saved
      const verifyDoc = await getDoc(stallDocRef);
      if (!verifyDoc.exists()) {
        throw new Error('Failed to verify stall was saved to pendingStalls');
      }
      console.log('Verified stall exists in pendingStalls');
      
      setCurrentStep(2);
    } catch (err: any) {
      console.error("Error during form submission:", err);
      setError('Failed to process form submission. ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePreviousStep = () => {
    setError(null);
    setCurrentStep((prev) => prev - 1);
  };

  useEffect(() => {
    const fetchLocationData = async () => {
      setLocationLoading(true);
      setLocationError(null);
      try {
        const querySnapshot = await getDocs(collection(db, 'administrativeUnits'));
        const fetchedData: LocationData = {};
        querySnapshot.forEach(doc => {
          const data = doc.data();
          if (!data['Region'] || !data['District/City'] || !data['Subcounty/Division'] || !data['Parish/Ward'] || !data['Village']) {
            console.warn("Skipping malformed administrative unit document:", doc.id);
            return;
          }
          const region = data['Region'];
          const district = data['District/City'];
          const subcounty = data['Subcounty/Division'];
          const parish = data['Parish/Ward'];
          const villages = [data['Village']];
          if (!fetchedData[region]) {fetchedData[region] = {}; }
          if (!fetchedData[region][district]) {fetchedData[region][district] = {}; }
          if (!fetchedData[region][district][subcounty]) {fetchedData[region][district][subcounty] = {}; }
          fetchedData[region][district][subcounty][parish] = villages;
        });
        setLocationData(fetchedData);
      } catch (err: any) {
        console.error("Error fetching location data:", err);
        setLocationError('Failed to load location data. Please try again.');
      } finally {
        setLocationLoading(false);
      }
    };
    fetchLocationData();
  }, []);

  useEffect(() => {
    const fetchStallNumber = async () => {
      setLoading(true);
      setError(null);
      try {
        const newStallNumber = await generateStallNumber();
        updateFormData({stallNumber: newStallNumber });
      } catch (err) {
        setError('Failed to generate stall number. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    if (!formData.stallNumber && !showTermsAndConditions && !locationLoading) {
      fetchStallNumber();
    }
  }, [formData.stallNumber, updateFormData, showTermsAndConditions, locationLoading]);

  // Fetch base prices from Firestore
  useEffect(() => {
    const fetchBasePrices = async () => {
      setPricesLoading(true);
      try {
        const querySnapshot = await getDocs(collection(db, 'stallPrices'));
        const prices: { Premium: number; Standard: number } = { ...basePrices };
        querySnapshot.forEach(doc => {
          const data = doc.data();
          if (data.stallType === 'Premium') {
            prices.Premium = data.price;
          } else if (data.stallType === 'Standard') {
            prices.Standard = data.price;
          }
        });
        setBasePrices(prices);
      } catch (err) {
        console.error('Error fetching base prices:', err);
        // Keep default prices if fetch fails
      } finally {
        setPricesLoading(false);
      }
    };
    fetchBasePrices();
  }, []);

  // Handle loading states for location data
  if (locationLoading) {
    return (
      <BuyStallPageContainer>
        <Spinner />
        <MessageContainer type="info">Loading location data...</MessageContainer>
      </BuyStallPageContainer>
    );
  }

  if (locationError) {
    return (
      <BuyStallPageContainer>
        <MessageContainer type="error">{locationError}</MessageContainer>
      </BuyStallPageContainer>
    );
  }

  return (
    <BuyStallPageContainer>
      {showTermsAndConditions ? (
        <TermsAndConditionsModal onAgree={handleAgreeToTerms} />
      ) : (
        <>
          <h2>Buy a Stall</h2>
          {error && <MessageContainer type="error">{error}</MessageContainer>}
          {successMessage && <MessageContainer type="success">{successMessage}</MessageContainer>}
          {loading && <Spinner />}

          {currentStep === 1 && locationData && (
            <StallRegistrationForm
              formData={formData}
              setFormData={updateFormData}
              onPayment={handleFormSubmit}
              locationData={locationData}
            />
          )}

          {currentStep === 2 && (
            <StallPaymentReview
              formData={formData}
              onPrevious={handlePreviousStep}             
              loading={loading}
              subscriptionAmount={subscriptionAmount}
              subscriptionDurationMonths={getDurationInDays(formData.subscriptionDuration) / 30}
            />
          )}
        </>
      )}
    </BuyStallPageContainer>
  );
};

export default BuyStall;